import React, { useState, useEffect, useContext } from 'react';
import { CheckCircle2, XCircle, Clock, AlertTriangle, FileText, Building, Phone, Mail, CreditCard, Landmark, User } from 'lucide-react';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';

const DesignerKYC = () => {
  const { authToken } = useContext(AuthContext);
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pan: '',
    bank_account_number: '',
    ifsc_code: '',
    gstin: '',
    business_type: 'individual',
    // Address fields
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'IN',
    business_category: 'fashion',
    business_subcategory: 'apparel'
  });

  useEffect(() => {
    fetchKYCData();
  }, [authToken]);

  const fetchKYCData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://vasstrinfo.com/api/designer/profile/', {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data?.data) {
        const data = response.data.data;
        setKycData(data);
        setFormData({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          pan: data.pan || '',
          bank_account_number: data.bank_account_number || '',
          ifsc_code: data.ifsc_code || '',
          gstin: data.gstin || '',
          business_type: data.business_type || 'individual',
          street_address_1: data.street_address_1 || '',
          street_address_2: data.street_address_2 || '',
          city: data.city || '',
          state: data.state || '',
          postal_code: data.postal_code || '',
          country: data.country || 'IN',
          business_category: data.business_category || 'fashion',
          business_subcategory: data.business_subcategory || 'apparel'
        });
      }
    } catch (error) {
      console.error('Error fetching KYC data:', error);
      if (error.response?.status === 404) {
        // No KYC data exists yet
        setKycData(null);
      } else {
        Swal.fire('Error', 'Failed to fetch KYC data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate PAN format based on business type
  const validatePANFormat = () => {
    if (!formData.pan || formData.pan.length !== 10) return null;

    const fourthChar = formData.pan[3]?.toUpperCase();

    if (formData.business_type === 'company' && fourthChar !== 'C') {
      return 'error';
    } else if ((formData.business_type === 'individual' || formData.business_type === 'proprietorship') && fourthChar !== 'P') {
      return 'error';
    }
    return 'valid';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post('https://vasstrinfo.com/api/designer/onboarding/', formData, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      Swal.fire({
        title: 'Success!',
        text: kycData ? 'KYC details updated successfully!' : 'KYC submission successful! Your application is under review.',
        icon: 'success',
        confirmButtonColor: '#ea580c'
      });

      setIsEditing(false);
      fetchKYCData();
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message || 'An error occurred during submission';
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#ea580c'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-6 w-6 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'under_review':
        return <Clock className="h-6 w-6 text-orange-500" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'under_review':
        return 'Under Review';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'under_review':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-600">Loading KYC information...</p>
          </div>
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Designer KYC</h1>
            <p className="text-lg text-gray-600">Know Your Customer verification for VasstrInfo Designer Program</p>
          </div>

          {/* Status Card */}
          {kycData && (
            <div className={`rounded-lg border-2 p-6 mb-8 ${getStatusColor(kycData.onboarding_status)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(kycData.onboarding_status)}
                  <div>
                    <h3 className="text-lg font-semibold">KYC Status: {getStatusText(kycData.onboarding_status)}</h3>
                    <p className="text-sm opacity-80">
                      {kycData.onboarding_status === 'approved' && 'Your KYC has been approved. You can now receive payouts.'}
                      {kycData.onboarding_status === 'under_review' && 'Your KYC is being reviewed by our team. This typically takes 1-2 business days.'}
                      {kycData.onboarding_status === 'rejected' && 'Your KYC was rejected. Please contact support or resubmit with correct information.'}
                      {kycData.onboarding_status === 'pending' && 'Please complete your KYC to start receiving payouts.'}
                    </p>
                  </div>
                </div>
                {kycData && !isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    Edit Details
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                {kycData ? (isEditing ? 'Edit KYC Details' : 'Your KYC Details') : 'Submit KYC Details'}
              </h2>
              {kycData && !isEditing && (
                <p className="text-orange-100 text-sm mt-1">
                  Your KYC information has been submitted. You can edit details if needed.
                </p>
              )}
            </div>

            <div className="p-6">
              {!kycData || isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="h-5 w-5 mr-2 text-orange-500" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        >
                          <option value="individual">Individual / Sole Proprietor</option>
                          <option value="proprietorship">Proprietorship</option>
                          <option value="company">Private Limited Company</option>
                          <option value="partnership">Partnership</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Select the legal structure of your business
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Building className="h-5 w-5 mr-2 text-orange-500" />
                      Business Address
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Street Address Line 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="street_address_1"
                          value={formData.street_address_1}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Street Address Line 2 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="street_address_2"
                          value={formData.street_address_2}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Postal Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          maxLength="6"
                          placeholder="500001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tax Information */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-orange-500" />
                      Tax Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PAN Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="pan"
                          value={formData.pan}
                          onChange={handleInputChange}
                          placeholder={formData.business_type === 'company' ? "AAAPC1234F" : "AAAAP1234F"}
                          maxLength="10"
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 uppercase ${
                            validatePANFormat() === 'error'
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : validatePANFormat() === 'valid'
                              ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                              : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
                          }`}
                          required
                        />
                        {validatePANFormat() === 'error' && (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            ⚠️ Invalid PAN for {formData.business_type === 'company' ? 'Company' : 'Individual/Proprietorship'}!
                            4th character must be '{formData.business_type === 'company' ? 'C' : 'P'}'
                          </p>
                        )}
                        {validatePANFormat() === 'valid' && (
                          <p className="text-xs text-green-600 mt-1 font-medium">
                            ✓ Valid PAN format
                          </p>
                        )}
                        {!validatePANFormat() && formData.business_type === 'company' ? (
                          <p className="text-xs text-blue-600 mt-1 font-medium">
                            ℹ️ For Company/Private Limited: Use Company PAN (4th character must be 'C')
                          </p>
                        ) : !validatePANFormat() && (
                          <p className="text-xs text-gray-500 mt-1">
                            For Individual/Proprietorship: Use Personal PAN (4th character must be 'P')
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          GSTIN (Optional)
                        </label>
                        <input
                          type="text"
                          name="gstin"
                          value={formData.gstin}
                          onChange={handleInputChange}
                          placeholder="22AAAAA0000A1Z5"
                          maxLength="15"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Banking Information */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Landmark className="h-5 w-5 mr-2 text-orange-500" />
                      Banking Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="bank_account_number"
                          value={formData.bank_account_number}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          IFSC Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleInputChange}
                          placeholder="ABCD0123456"
                          maxLength="11"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end space-x-4 pt-6">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        disabled={isSubmitting}
                        className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-md flex items-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>{kycData ? 'Update Details' : 'Submit KYC'}</span>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                /* Display Mode */
                <div className="space-y-6">
                  {/* Personal Information Display */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="h-5 w-5 mr-2 text-orange-500" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                        <p className="text-gray-900 font-medium">{kycData.name || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                        <p className="text-gray-900 font-medium">{kycData.email || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                        <p className="text-gray-900 font-medium">{kycData.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Business Type</label>
                        <p className="text-gray-900 font-medium capitalize">{kycData.business_type || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information Display */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Building className="h-5 w-5 mr-2 text-orange-500" />
                      Business Address
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Street Address Line 1</label>
                        <p className="text-gray-900 font-medium">{kycData.street_address_1 || 'Not provided'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Street Address Line 2</label>
                        <p className="text-gray-900 font-medium">{kycData.street_address_2 || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">City</label>
                        <p className="text-gray-900 font-medium">{kycData.city || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">State</label>
                        <p className="text-gray-900 font-medium">{kycData.state || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Postal Code</label>
                        <p className="text-gray-900 font-medium">{kycData.postal_code || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
                        <p className="text-gray-900 font-medium">{kycData.country || 'IN'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Tax Information Display */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-orange-500" />
                      Tax Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">PAN Number</label>
                        <p className="text-gray-900 font-medium font-mono">{kycData.pan || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">GSTIN</label>
                        <p className="text-gray-900 font-medium font-mono">{kycData.gstin || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Banking Information Display */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Landmark className="h-5 w-5 mr-2 text-orange-500" />
                      Banking Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Bank Account Number</label>
                        <p className="text-gray-900 font-medium font-mono">
                          {kycData.bank_account_number ? `****${kycData.bank_account_number.slice(-4)}` : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">IFSC Code</label>
                        <p className="text-gray-900 font-medium font-mono">{kycData.ifsc_code || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Important Notes:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>All information provided must be accurate and verifiable</li>
                  <li>KYC verification typically takes 1-2 business days</li>
                  <li>You can edit your details only after initial submission</li>
                  <li>Payouts will only be processed after KYC approval</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    } />
  );
};

export default DesignerKYC;